import type { PhaseConfig } from 'boardgame.io';
import { debug } from '../../utils/debug';
import type { SeattleGameState } from '../../types';
import { selectActionSeattle, confirmSelectionSeattle, cancelSelectionSeattle } from '../moves/selectActionsSeattle';
import { undoToTurnStart } from '../moves/undoToTurnStart';

/**
 * Phase 3: アクション選択フェーズ（Seattle固有）
 *
 * ソロプレイヤーは2種類のアクション（Engineer, Locomotive）から選択します。
 * - 1ターンに両方・片方・どちらも選ばなくても構いません
 * - 手数料は発生しません（清算時にまとめて支払い）
 * - Locomotive は効果選択が必要（機関車+1 OR 貨物輸送Lv+1）
 * - Confirm ボタンで手動確定（自動終了なし）
 * - Cancel ボタンで選択をリセット可能
 *
 * @see docs/specifications/logic/seattle_metro_solo/phase_03_select_actions_seattle.md
 */
export const selectActionsSeattlePhase: PhaseConfig<SeattleGameState> = {
  moves: {
    selectAction: selectActionSeattle,
    confirmSelection: confirmSelectionSeattle,
    cancelSelection: cancelSelectionSeattle,
    undoToTurnStart: undoToTurnStart as any,
  },

  // Phase開始時の処理
  onBegin: ({ G }: { G: SeattleGameState }) => {
    debug.log(`[selectActionsSeattlePhase] Phase 3: Select Actions started (Turn ${G.actualTurn})`);

    const player = G.players['0'];

    // 前ターンのフラグをリセット
    player.hasPassed = false;
    player.selectedAction = null;
    player.actionsSelectedThisTurn = [];
    player.locomotiveEffectThisTurn = undefined;

    debug.log(`[selectActionsSeattlePhase] Player state reset: engineerUsageCount=${player.engineerUsageCount}, locomotiveUsageCount=${player.locomotiveUsageCount}`);
  },

  // Phase終了条件: Confirm 押下（hasPassed）または Undo 強制遷移
  endIf: ({ G }: { G: SeattleGameState }) => {
    // Undo 強制遷移フラグが立っている場合は即座に終了
    if (G.forcePhaseToIssueShares) {
      debug.log(`[selectActionsSeattlePhase] Force transition to issueShares (Undo)`);
      return true;
    }

    const player = G.players['0'];

    // プレイヤーが Confirm を押した場合のみ Phase 終了
    if (player.hasPassed) {
      debug.log(`[selectActionsSeattlePhase] Phase ending, player confirmed with actions: ${player.actionsSelectedThisTurn.join(', ') || 'none'}`);
      return true;
    }

    return false;
  },

  // Phase終了時の処理
  onEnd: ({ G }: { G: SeattleGameState }) => {
    const player = G.players['0'];
    debug.log(`[selectActionsSeattlePhase] Phase ended, transitioning to buildTrack`);
    debug.log(`[selectActionsSeattlePhase] Final selection: ${player.actionsSelectedThisTurn.join(', ') || 'none'}`);
  },

  // Phase 4 へ遷移、または Undo 時は issueShares へ
  next: ({ G }: { G: SeattleGameState }) => {
    if (G.forcePhaseToIssueShares) {
      return 'issueShares';
    }
    return 'buildTrack';
  }
};
